/** QuoteStaff group (canonical `tFr3sePv`, spec §7). Bearer JWT, admin only.
 *  Every call carries the staff token automatically (group === "staff"). */

import { api } from "./client";
import { normaliseLineItem, normaliseLineItems } from "@/lib/lineItems";
import type {
  Quote,
  LineItem,
  LineItemUpdate,
  QuoteTemplate,
  QuoteListFilters,
  ImageUploadUrlResponse,
  QuoteEngagement,
  AcceptOnBehalfResponse,
} from "@/types";

export interface QuoteListResponse {
  items: Quote[];
  /** Xano paging metadata is permissive across deployments; keep it loose. */
  itemsTotal?: number;
  pageTotal?: number;
  page?: number;
  perPage?: number;
}

/** 190 GET quote — list with filters + paging. */
export function listQuotes(filters: QuoteListFilters = {}): Promise<QuoteListResponse | Quote[]> {
  return api.get<QuoteListResponse | Quote[]>("staff", "quote", {
    query: {
      status: filters.status,
      site_postcode: filters.site_postcode,
      email: filters.email,
      client_id: filters.client_id,
      job_id: filters.job_id,
      open_only: filters.open_only,
      page: filters.page,
      per_page: filters.per_page,
    },
  });
}

/** 191 GET quote/{id} — single quote with line_items included. */
export function getQuote(quoteId: number): Promise<Quote> {
  return api.get<Quote>("staff", `quote/${quoteId}`).then(normaliseLineItems);
}

/** 192 POST quote — create draft (auto quote_number, 30-day expiry default). */
export function createQuote(body: Partial<Quote>): Promise<Quote> {
  return api.post<Quote>("staff", "quote", { body });
}

/** 193 PATCH quote/{id} — partial update; send only changing fields. */
export function updateQuote(quoteId: number, body: Partial<Quote>): Promise<Quote> {
  return api.patch<Quote>("staff", `quote/${quoteId}`, { body });
}

/** 194 POST quote/{id}/line-items — add a line item. */
export function addLineItem(quoteId: number, body: Partial<LineItem>): Promise<LineItem> {
  return api.post<LineItem>("staff", `quote/${quoteId}/line-items`, { body }).then(normaliseLineItem);
}

/** 195 PATCH quote-line-items/{id} — update a line item via a JSON-patch
 *  envelope: { patch: { ...changed fields... } }. Never send quantity/line_total;
 *  the server forces quantity = 1 and derives line_total = unit_price. Returns
 *  the updated row (incl. the server-derived line_total). */
export function updateLineItem(lineItemId: number, body: LineItemUpdate): Promise<LineItem> {
  return api
    .patch<LineItem>("staff", `quote-line-items/${lineItemId}`, { body })
    .then(normaliseLineItem);
}

/** 196 DELETE quote-line-items/{id} — delete a line item. */
export function deleteLineItem(lineItemId: number): Promise<unknown> {
  return api.delete<unknown>("staff", `quote-line-items/${lineItemId}`);
}

/** 197 POST quote/{id}/publish — mints slug, 30-day expiry, draft→sent.
 *  Idempotent; refuses terminal. */
export function publishQuote(quoteId: number): Promise<Quote> {
  return api.post<Quote>("staff", `quote/${quoteId}/publish`);
}

/** 198 POST quote/{id}/withdraw — → withdrawn. Idempotent;
 *  refuses accepted/declined/converted. */
export function withdrawQuote(quoteId: number): Promise<Quote> {
  return api.post<Quote>("staff", `quote/${quoteId}/withdraw`);
}

/** 332 POST quote/{id}/reopen — expired/declined → back to sent with a fresh
 *  30-day expiry (slug kept, so the link already with the client keeps
 *  working). Also returns any lead the quote's closure auto-parked in "lost"
 *  to the pipeline's quoted column. Idempotent; refuses
 *  draft/withdrawn/accepted/converted. */
export function reopenQuote(quoteId: number): Promise<Quote> {
  return api.post<Quote>("staff", `quote/${quoteId}/reopen`);
}

/** 345 POST quote/{id}/accept-on-behalf — record an acceptance that arrived
 *  outside the quote page (typically an email reply). Same guards and
 *  accepted_total snapshot as the public accept, but staff-audited: the view
 *  event is is_staff and internal_notes gains a dated line naming the acting
 *  user. Refuses draft/withdrawn/declined/expired (publish or reopen first);
 *  idempotent on an already-accepted quote. */
export function acceptQuoteOnBehalf(
  quoteId: number,
  acceptedByName: string,
  selectedOptionalLineItemIds: number[],
): Promise<AcceptOnBehalfResponse> {
  return api.post<AcceptOnBehalfResponse>("staff", `quote/${quoteId}/accept-on-behalf`, {
    body: {
      accepted_by_name: acceptedByName,
      selected_optional_line_item_ids: selectedOptionalLineItemIds,
    },
  });
}

/** 335 GET quote/{id}/engagement — the engagement panel payload: per-session dwell, scroll and
 *  section totals from the public page's beacon, staff sessions excluded server-side. */
export function getQuoteEngagement(quoteId: number): Promise<QuoteEngagement> {
  return api.get<QuoteEngagement>("staff", `quote/${quoteId}/engagement`);
}

/** 199 POST quote/from-template/{templateId} — clone template into a new draft.
 *  Returns quote + line_items. Pre-fill client/site fields via body. */
export function createFromTemplate(
  templateId: number,
  body: Partial<Quote> = {},
): Promise<Quote> {
  return api
    .post<Quote>("staff", `quote/from-template/${templateId}`, { body })
    .then(normaliseLineItems);
}

/** 200 POST template — create template (name, content, default_line_items). */
export function createTemplate(body: Partial<QuoteTemplate>): Promise<QuoteTemplate> {
  return api.post<QuoteTemplate>("staff", "template", { body });
}

/** 201 GET templates — list all (active + retired), sorted by name. */
export function listTemplates(): Promise<QuoteTemplate[]> {
  return api.get<QuoteTemplate[]>("staff", "templates");
}

/** POST templates/set-default — set the default template (clears it on all
 *  others; server enforces at-most-one). template_id 0 clears the default.
 *  Returns the full template list with updated is_default. */
export function setDefaultTemplate(templateId: number): Promise<QuoteTemplate[]> {
  return api.post<QuoteTemplate[]>("staff", "templates/set-default", {
    body: { template_id: templateId },
  });
}

/** 202 PATCH template/{id} — full-object update; toggle is_active to retire. */
export function updateTemplate(
  templateId: number,
  body: Partial<QuoteTemplate>,
): Promise<QuoteTemplate> {
  return api.patch<QuoteTemplate>("staff", `template/${templateId}`, { body });
}

/** 203 POST quote/image-upload-url — mint a one-time Cloudflare direct-upload
 *  URL (§6). Returns { uploadURL, id }. Mint at upload time (it expires ~30 min).
 *  Returns a clean error when Cloudflare Images isn't configured. */
export function mintImageUploadUrl(): Promise<ImageUploadUrlResponse> {
  return api.post<ImageUploadUrlResponse>("staff", "quote/image-upload-url");
}
